
import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Terrace } from "@/types/terrace";
import { UseFormReturn } from "react-hook-form";
import { User } from "lucide-react";
import TerraceBasicInfoForm from "./TerraceBasicInfoForm";
import OpeningHoursForm from "./OpeningHoursForm";
import TerraceFeatures from "./TerraceFeatures";
import CoordinatesDisplay from "./CoordinatesDisplay";
import TerracePhotoManager from "./TerracePhotoManager";

interface EditTerraceDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  editingTerrace: Terrace | null;
  form: UseFormReturn<any>;
  onSave: () => void;
}

const EditTerraceDialog = ({ 
  isOpen, 
  onOpenChange, 
  editingTerrace, 
  form, 
  onSave 
}: EditTerraceDialogProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Terrace</DialogTitle>
          <DialogDescription>
            Make changes to the terrace information below.
            {editingTerrace?.submitted_by && (
              <span className="flex items-center gap-1 mt-2 text-sm">
                <User className="h-3 w-3" />
                Originally submitted by: <span className="font-medium">{editingTerrace.submitted_by}</span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        {editingTerrace && (
          <Form {...form}>
            <div className="grid gap-4 py-4">
              <TerraceBasicInfoForm form={form} terraceId={editingTerrace.id} />
              
              {/* Display coordinates under the address */}
              {editingTerrace.lat && editingTerrace.lng && (
                <div className="pl-4 border-l-2 border-sky-mist/30">
                  <div className="text-sm text-muted-foreground mb-2">Location Coordinates</div>
                  <CoordinatesDisplay 
                    lat={editingTerrace.lat} 
                    lng={editingTerrace.lng} 
                    className="mb-2"
                  />
                </div>
              )}

              <TerracePhotoManager 
                form={form} 
                currentPhotoUrl={editingTerrace.photo_url}
                terraceId={editingTerrace.id}
                terraceName={editingTerrace.name}
              />
              
              <OpeningHoursForm form={form} />
              <TerraceFeatures form={form} />
            </div>
          </Form>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave}>Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditTerraceDialog;
